import { Control } from '@peckadesign/pd-naja/dist/types'
import Collapsable, { deepMerge } from 'collapsable.js'
import { CollapsableEvent, CollapsableOptions } from 'collapsable.js/dist/Collapsable'
import { HTMLCollapsableItem } from 'collapsable.js/dist/CollapsableItem'
import { DeepPartial } from 'collapsable.js/dist/utils'

type CollapsableGroup = {
	selector: string
	offTapClose: boolean
	options?: DeepPartial<CollapsableOptions>
}

class CollapsableControl implements Control {
	private offTapClose: HTMLCollapsableItem[] = []
	private groups: CollapsableGroup[] = [
		{
			selector:
				'.js-collapsable:not(' +
				'	.js-collapsable--tabs,' +
				'	.js-collapsable--off-tap-close,' +
				'	.js-collapsable--accordion,' +
				'	[data-collapsable-overlay]' +
				')',
			offTapClose: false
		},
		{
			selector: '.js-collapsable-tabs .js-collapsable--tabs',
			offTapClose: false,
			options: {
				accordion: true,
				collapsableAll: false,
				externalLinks: {
					preventDefault: true
				}
			}
		},
		{
			selector: '.js-collapsable-accordion .js-collapsable--accordion',
			offTapClose: false,
			options: {
				accordion: true
			}
		},
		{
			selector: '.js-collapsable--off-tap-close, [data-collapsable-overlay]',
			offTapClose: true
		}
	]

	private commonOptions: Partial<CollapsableOptions> = {}

	public constructor() {
		this.attachHandlers()
	}

	public initialize(context: Element | Document): void {
		this.purgeOffTapClose()

		this.groups.forEach((group) => {
			const collapsableElements = context.querySelectorAll<HTMLElement>(`:where(${group.selector})`)

			if (collapsableElements.length === 0) {
				return
			}

			// If off tapping should close, we need to initialize items as separate Collapsable, otherwise
			// when `collapsableElements.length > 1`, off tap would close all items regardless of clicked target.
			if (group.offTapClose) {
				collapsableElements.forEach((element) => {
					new Collapsable(element, deepMerge({}, this.commonOptions, group.options))

					this.initializeElement(element as HTMLCollapsableItem)
					this.offTapClose.push(element as HTMLCollapsableItem)
				})
			} else {
				new Collapsable(collapsableElements, deepMerge({}, this.commonOptions, group.options))
				collapsableElements.forEach((element) => {
					this.initializeElement(element as HTMLCollapsableItem)
				})
			}
		})
	}

	private initializeElement(element: HTMLCollapsableItem): void {
		if (element.dataset.collapsableHasAutofocus !== undefined) {
			element.addEventListener('expanded.collapsable', this.handleAutofocus.bind(this))
		}

		if (element.dataset.collapsableScrollIntoView !== undefined) {
			element.addEventListener(element.dataset.collapsableScrollIntoView, this.handleScrollIntoView.bind(this))
		}
	}

	private attachHandlers(): void {
		// Off tap close, we're listening during the capture phase, so this is handled before the overlay handlers.
		document.body.addEventListener('click', this.handleOffTapClose.bind(this), { capture: true })

		// Overlay
		document.body.addEventListener('expand.collapsable', this.handleOverlayClass.bind(this))
		document.body.addEventListener('collapse.collapsable', this.handleOverlayClass.bind(this))
		document.body.addEventListener('destroy.collapsable', this.handleOverlayClass.bind(this))
	}

	private handleOverlayClass(event: CollapsableEvent): void {
		const overlayClass = event.target && (event.target as HTMLElement).dataset.collapsableOverlay

		if (overlayClass) {
			const action = event.type === 'expand.collapsable' ? 'add' : 'remove'

			document.body.classList[action](overlayClass)
		}
	}

	private purgeOffTapClose(): void {
		this.offTapClose = this.offTapClose.filter((collapsableItem) => collapsableItem.isConnected)
	}

	private handleOffTapClose(event: Event): void {
		if (!event.target) {
			return
		}

		const target = event.target as HTMLElement

		this.offTapClose.forEach((element: HTMLCollapsableItem) => {
			if (element.contains(target)) {
				return
			}

			const extLinks = element.collapsableItem.collapsable
				.getExtLinkById(element.id)
				.filter((extLink) => extLink.extLink.contains(target))

			if (extLinks.length) {
				return
			}

			element.collapsableItem.collapsable.collapseAll()
		})
	}

	private handleAutofocus(event: Event): void {
		const collapsableItemElement = event.target as HTMLCollapsableItem
		const autofocus = collapsableItemElement.querySelector<HTMLInputElement>(
			'[autofocus], [data-collapsable-autofocus]'
		)

		if (!autofocus) {
			return
		}

		autofocus.focus({
			preventScroll: true
		})

		setTimeout(() => {
			autofocus.scrollIntoView({ block: 'start' })
		}, 0)
	}

	private handleScrollIntoView(event: Event): void {
		const collapsableItemElement = event.target as HTMLCollapsableItem
		collapsableItemElement.collapsableItem.boxElements[0].scrollIntoView({
			block: 'nearest'
		})
	}
}

export default new CollapsableControl()
